import { ShoppingBag, Store } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import { getProductAlt } from "../lib/productAlt";
import ProductImage from "./ProductImage";

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { recordView } = useRecentlyViewed();
  const isInStoreOnly = !!product.in_store_only;
  const isSoldOut = !!product.sold_out;

  const handleAdd = () => {
    addItem(product);
    recordView(product.id);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="product-card glass rounded-3xl overflow-hidden border border-white/10 flex flex-col"
    >
      <div className="relative overflow-hidden">
        <ProductImage
          src={product.image}
          alt={getProductAlt(product)}
          size="lg"
          className="w-full h-64 object-cover transition-transform duration-700 hover:scale-105"
        />
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full glass border border-[#00b4ff]/50 text-[#00b4ff]">
          {product.category.replace(/-/g, " ")}
        </div>
        {isSoldOut ? (
          <div
            data-testid={`badge-sold-out-${product.id}`}
            className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full glass border border-[#ff1a8c]/60 text-[#ff1a8c] font-semibold"
          >
            Sold Out
          </div>
        ) : isInStoreOnly && (
          <div
            data-testid={`badge-in-store-${product.id}`}
            className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full glass border border-[#ff1a8c]/60 text-[#ff1a8c] flex items-center gap-1"
          >
            <Store className="w-3 h-3" /> In store
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1 gap-3">
        <h3 className="font-script text-2xl text-white">{product.name}</h3>
        <p className="text-sm text-gray-400 leading-relaxed flex-1">{product.description}</p>
        <div className="flex items-center justify-between mt-2">
          <span data-testid={`product-price-${product.id}`} className="text-xl font-bold neon-blue">
            ${product.price.toFixed(2)}
          </span>
          {isSoldOut ? (
            <button
              data-testid={`sold-out-btn-${product.id}`}
              disabled
              className="neon-btn !px-4 !py-2 opacity-40 cursor-not-allowed"
              aria-label={`${product.name} is sold out`}
            >
              Sold Out
            </button>
          ) : isInStoreOnly ? (
            <span
              data-testid={`in-store-label-${product.id}`}
              className="text-[11px] uppercase tracking-[0.2em] text-gray-400"
            >
              Visit us to try
            </span>
          ) : (
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleAdd}
              className="neon-btn neon-btn--blue !px-4 !py-2"
              aria-label={`Add ${product.name} to cart`}
            >
              <ShoppingBag className="w-4 h-4" /> Add
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
